import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@roger/ui-components';
import { formatDateTimeWithTimezoneFallback } from '../../utils/dateUtils';
import { claimAgeDays, daysToPayDays } from './claimAge';

interface ClaimAgeCellProps {
  /** billing-period end (YYYY-MM-DD); used for live Claim Age */
  periodEnd?: string | null;
  /** 'age' (default) shows live Claim Age; 'daysToPay' shows frozen billed→paid */
  mode?: 'age' | 'daysToPay';
  createdAt?: string | null;
  billedAt?: string | null;
  paidDate?: string | null;
}

const TIME_FORMAT = 'M/d/yy, h:mm a';

const PopoverRow: React.FC<{ label: string; iso?: string | null }> = ({
  label,
  iso,
}) =>
  iso ? (
    <div className='flex justify-between gap-6 text-xs'>
      <span className='uppercase tracking-wide text-muted-foreground'>
        {label}
      </span>
      <span>{formatDateTimeWithTimezoneFallback(iso, TIME_FORMAT)}</span>
    </div>
  ) : null;

export const ClaimAgeCell: React.FC<ClaimAgeCellProps> = ({
  periodEnd,
  mode = 'age',
  createdAt,
  billedAt,
  paidDate,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  // All day counts are measured in the viewer's local timezone, consistent with
  // the popover (which renders the timestamps in local time too).
  const days =
    mode === 'daysToPay'
      ? daysToPayDays(billedAt, paidDate)
      : claimAgeDays(periodEnd);

  const value = days === null ? '—' : `${days}d`;

  const hasPopover = Boolean(createdAt || billedAt || paidDate);

  const open = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };
  const close = () => {
    timeoutRef.current = setTimeout(() => setIsOpen(false), 150);
  };

  if (!hasPopover) {
    return <span>{value}</span>;
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <span
          className='cursor-help font-semibold underline decoration-dotted underline-offset-4'
          onClick={e => e.stopPropagation()}
          onMouseEnter={open}
          onMouseLeave={close}
        >
          {value}
        </span>
      </PopoverTrigger>
      <PopoverContent
        side='top'
        align='center'
        className='z-50 w-auto space-y-1.5 p-3'
        onMouseEnter={open}
        onMouseLeave={close}
      >
        <PopoverRow label='Created' iso={createdAt} />
        <PopoverRow label='Billed' iso={billedAt} />
        <PopoverRow label='Paid' iso={paidDate} />
      </PopoverContent>
    </Popover>
  );
};
